import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, NavLink } from "react-router-dom";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { NumericFormat } from "react-number-format";
import { useToast } from "../../../context/ToastContext";
import { useAuthContext } from "../../../hooks/useAuthContext";
import { msmartAxios } from "../../../api/axios";
import useTeamAccess from "../hooks/useTeamAccess";
import { FollowUpBodySkeleton } from "../components/MsmartSkeletons";
import ErrorState from "../../../components/ErrorState";
import { getErrorMessage } from "../../../utils/errorMessage";


function ManagerFollowUp() {
  const { user } = useAuthContext();
  const { teamId } = useParams();
  const { notifyError, notifySuccess } = useToast();


  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState([]);

  // UI states
  const [searchName, setSearchName] = useState("");
  const [onlyFuture, setOnlyFuture] = useState(false);

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // edit modal states
  const [selectedLead, setSelectedLead] = useState(null);
  const [editedStatus, setEditedStatus] = useState("");
  const [editedRemark, setEditedRemark] = useState("");
  const [edittedFuDate, setEditedFuDate] = useState(null);
  const [editClosedAmount, setEditClosedAmount] = useState("");

  // default date range: today
  const today = useMemo(() => {
    const t = new Date();
    t.setMinutes(t.getMinutes() - t.getTimezoneOffset());
    return t;
  }, []);

  const todayStr = today.toISOString().split("T")[0];
  const [filterDate, setFilterDate] = useState({
    startDate: todayStr,
    endDate: todayStr,
  });

  const statusOption = useMemo(
    () => [
      { value: "No Status", label: "No Status" },
      { value: "Closed", label: "Closed" },
      { value: "Booking", label: "Booking" },
      { value: "Rejected", label: "Rejected" },
      { value: "Follow Up", label: "Follow Up" },
    ],
    []
  );

  // ===== helpers =====
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatFollowUp12h = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const isFutureFollowUp = (iso) => {
    if (!iso) return false;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return false;
    return d.getTime() > Date.now();
  };

  // ===== permission check =====
  const access = useTeamAccess(teamId);
  const view = access.view;

  // ===== fetch data =====
  const fetchData = async (startDate, endDate) => {
    setLoading(true);
    try {
      const res = await msmartAxios.post(
        `/api/msmart/get/followup/${teamId}`,
        { startDate, endDate },
        { headers: { accessToken: user.token } }
      );
      setLead(Array.isArray(res.data) ? res.data : []);
      setCurrentPage(1); // reset page
    } catch (err) {
      notifyError(getErrorMessage(err, "Unable to fetch follow up."));
      setLead([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filterDate.startDate && filterDate.endDate) {
      fetchData(filterDate.startDate, filterDate.endDate);
    }
    // eslint-disable-next-line
  }, [filterDate.startDate, filterDate.endDate]);

  const handleDateChange = (selectedDates) => {
    if (selectedDates.length === 2) {
      const start = selectedDates[0];
      const end = selectedDates[1];
      const formattedStart = formatLocalDate(start);
      const formattedEnd = formatLocalDate(end);
      setFilterDate({ startDate: formattedStart, endDate: formattedEnd });
    }
  };

  const setDefault = () => {
    setEditedStatus("");
    setEditedRemark("");
    setEditedFuDate(null);
    setEditClosedAmount("");
    setSelectedLead(null);
  };

  const handleLeadClick = (leadId) => {
    const found = lead.find((x) => x.id === leadId);
    if (!found) return;

    setSelectedLead(found);
    setEditedStatus(found.status || "No Status");
    setEditedRemark(found.remark || "");
    setEditClosedAmount(found.salesAmount || "");
    setEditedFuDate(found.followUpDate ? String(found.followUpDate).slice(0, 16) : null);
  };

  const handleSubmit = async (id) => {
    if (editedStatus === "Follow Up" && !edittedFuDate) {
      notifyError(
        "Error: No Next Follow Up Date, please select other status than 'Follow Up'"
      );
      return;
    }
    if (editedStatus === "No Status" && edittedFuDate) {
      notifyError("Follow Up Date detected. Please select status other than 'No Status'");
      return;
    }

    const payload = {
      editedRemark,
      editedStatus,
      edittedFuDate,
      editClosedAmount,
    };

    try {
      const res = await msmartAxios.put(
        `/api/msmart/edit/followup/${teamId}/${id}`,
        payload,
        { headers: { accessToken: user.token } }
      );

      if (res.status === 201) {
        notifySuccess(res.data.succ || "Updated");
        // close modal (bootstrap)
        const closeBtn = document.getElementById("closemodal");
        closeBtn?.click();

        // refresh
        await fetchData(filterDate.startDate, filterDate.endDate);
        setDefault();
      } else {
        notifyError(res.data?.error || "Error updating lead");
      }
    } catch (err) {
      notifyError(getErrorMessage(err, "Error updating lead"));
    }
  };

  // ===== FILTERED DATA =====
  const filteredLeads = useMemo(() => {
    const q = searchName.trim().toLowerCase();

    return lead
      .filter((x) => {
        if (onlyFuture && !isFutureFollowUp(x.followUpDate)) return false;
        if (!q) return true;
        return String(x.name || "").toLowerCase().includes(q);
      })
      .sort((a, b) => new Date(a.followUpDate) - new Date(b.followUpDate)); // soonest first
  }, [lead, searchName, onlyFuture]);

  // ===== KPI =====
  const kpi = useMemo(() => {
    const total = filteredLeads.length;
    const future = filteredLeads.filter((x) => isFutureFollowUp(x.followUpDate)).length;
    const dueToday = filteredLeads.filter((x) => {
      const d = new Date(x.followUpDate);
      if (Number.isNaN(d.getTime())) return false;
      const now = new Date();
      return (
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate()
      );
    }).length;

    return { total, future, dueToday };
  }, [filteredLeads]);

  // ===== Pagination =====
  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / rowsPerPage));
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredLeads.slice(indexOfFirstRow, indexOfLastRow);

  const changePage = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) setCurrentPage(newPage);
  };

  return (
    <div className="container py-6 ss-page">
      <div className="row justify-content-center text-start">
        <div className="col-lg-12">
          <div>
            <h1 className="header-title mb-1">M-Smart</h1>
            <div className="text-muted" style={{ fontSize: 14 }}>
              Sales Activity Management.
            </div>
          </div>
        </div>
      </div>
  
      <div className="row justify-content-center">
              <div className="col-md-6">
                <div className="msmart-segment my-4">
                  <NavLink
                    to={`/msmart/team/summary/${teamId}`}
                    className={({ isActive }) =>
                      `msmart-seg-link ${isActive ? "active" : ""}`
                    }
                  >
                    Team
                  </NavLink>
      
                  <NavLink
                       to={'#'}// <-- route personal kau
                    className={({ isActive }) =>
                      `msmart-seg-link ${isActive ? "active" : ""}`
                    }
                  >
                    Personal
                  </NavLink>
                </div>
              </div>
            </div>

      <div className="card text-center mt-4 ds-card">
        <div className="card-header text-start">
          <ul className="nav nav-tabs card-header-tabs">
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/manager/database/${teamId}`}>
                Manage
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link active" to="#">
                Follow Up
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/manager/summary/${teamId}`}>
                Summary
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/manager/summary/${teamId}?view=analysis`}>Analysis</Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/manager/form/${teamId}`}>
                Form
              </Link>
            </li>
          </ul>
        </div>
  
        <div className="card-body">
          {access.error ? (
            <ErrorState title="Unable to verify access" message={access.error} className="py-2" />
          ) : view === null ? (
            <FollowUpBodySkeleton />
          ) : view === false ? (
            <div className="row justify-content-center mt-3">
              <div className="col-lg-6">
                <div className="alert alert-danger text-center" role="alert">
                  You do not have permission to access this team M-Smart page
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="card mb-3 followup-toolbar">
                <div className="card-body p-3">
                  <div className="d-flex flex-column flex-lg-row gap-2 justify-content-between align-items-lg-center">
                    <div className="d-flex flex-column flex-md-row gap-2 flex-grow-1">
                      <div className="form-floating flex-grow-1">
                        <Flatpickr
                          id="fuDateRange"
                          className="form-control"
                          placeholder="Select date range"
                          value={
                            filterDate.startDate && filterDate.endDate
                              ? [
                                  new Date(filterDate.startDate),
                                  new Date(filterDate.endDate),
                                ]
                              : []
                          }
                          options={{
                            mode: "range",
                            dateFormat: "d-m-Y",
                            disableMobile: true,
                          }}
                          onChange={handleDateChange}
                        />
                        <label htmlFor="fuDateRange">Date Range</label>
                      </div>
  
                      <div className="form-floating flex-grow-1">
                        <input
                          type="text"
                          className="form-control"
                          id="fuSearch"
                          placeholder="Search by name"
                          value={searchName}
                          onChange={(e) => setSearchName(e.target.value)}
                        />
                        <label htmlFor="fuSearch">Search by Name</label>
                      </div>
                    </div>
  
                    <div className="d-flex gap-2 justify-content-end align-items-center">
                      <button
                        type="button"
                        className={`btn btn-sm ${
                          onlyFuture ? "btn-dark" : "btn-outline-dark"
                        }`}
                        onClick={() => setOnlyFuture((v) => !v)}
                        title="Only show follow ups after current time"
                      >
                        <i className="bi bi-clock-history me-1"></i>
                        {onlyFuture ? "Future Only" : "All in Range"}
                      </button>
  
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() =>
                          fetchData(filterDate.startDate, filterDate.endDate)
                        }
                        disabled={loading}
                        title="Refresh"
                      >
                        <i className="bi bi-arrow-clockwise"></i>
                      </button>
                    </div>
                  </div>
  
                  {/* KPI chips */}
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <span className="ds-chip">
                      <i className="bi bi-list-check"></i> Total: <b>{kpi.total}</b>
                    </span>
                    <span className="ds-chip">
                      <i className="bi bi-calendar2-week"></i> Due Today:{" "}
                      <b>{kpi.dueToday}</b>
                    </span>
                    <span className="ds-chip ds-chip-dark">
                      <i className="bi bi-alarm"></i> Future: <b>{kpi.future}</b>
                    </span>
                  </div>
                </div>
              </div>
  
              {/* Table */}
              <div className="table-responsive" style={{ fontSize: "0.9rem" }}>
                <table className="table table-bordered align-middle followup-table">
                  <thead className="bg-light">
                    <tr>
                      <th style={{ width: 60 }}>#</th>
                      <th className="text-start">Name</th>
                      <th className="text-start" style={{ width: 170 }}>
                        Phone
                      </th>
                      <th style={{ width: 190 }}>Follow Up (12h)</th>
                      <th style={{ width: 170 }}>Action</th>
                    </tr>
                  </thead>
  
                  <tbody>
                    {loading ? (
                      Array.from({ length: rowsPerPage }).map((_, i) => (
                        <tr key={i}>
                          <td>
                            <div
                              className="skeleton-box"
                              style={{ height: 14, width: 24 }}
                            />
                          </td>
                          <td>
                            <div
                              className="skeleton-box"
                              style={{ height: 14, width: "55%" }}
                            />
                          </td>
                          <td>
                            <div
                              className="skeleton-box"
                              style={{ height: 14, width: "70%" }}
                            />
                          </td>
                          <td>
                            <div
                              className="skeleton-box"
                              style={{ height: 14, width: "75%" }}
                            />
                          </td>
                          <td>
                            <div
                              className="skeleton-box"
                              style={{
                                height: 32,
                                width: 120,
                                borderRadius: 12,
                              }}
                            />
                          </td>
                        </tr>
                      ))
                    ) : currentRows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-muted">
                          No Follow Up found.
                        </td>
                      </tr>
                    ) : (
                      currentRows.map((item, idx) => (
                        <tr key={item.id}>
                          <td>{idx + 1 + (currentPage - 1) * rowsPerPage}</td>
                          <td className="text-start fw-semibold">{item.name}</td>
                          <td className="text-start">
                            {item.country}
                            {item.phone}
                          </td>
                          <td>
                            <span
                              className={`badge rounded-pill ${
                                isFutureFollowUp(item.followUpDate)
                                  ? "bg-dark"
                                  : "bg-secondary"
                              }`}
                            >
                              {formatFollowUp12h(item.followUpDate)}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex gap-2 justify-content-center">
                              <a
                                className="btn btn-sm btn-outline-primary"
                                href={`tel:${item.country}${item.phone}`}
                                title="Call"
                              >
                                <i className="bi bi-telephone-forward"></i>
                              </a>
                              <a
                                className="btn btn-sm btn-outline-success"
                                href={`https://wa.me/${item.country}${item.phone}`}
                                target="_blank"
                                rel="noreferrer"
                                title="WhatsApp"
                              >
                                <i className="bi bi-whatsapp"></i>
                              </a>
  
                              {/* ✅ Modal trigger kekal sama */}
                              <button
                                type="button"
                                className="btn btn-sm btn-dark"
                                onClick={() => handleLeadClick(item.id)}
                                data-bs-toggle="modal"
                                data-bs-target="#leadDetail"
                                title="Update"
                              >
                                <i className="bi bi-check-lg"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
  
              {/* Pagination */}
              <div className="d-flex justify-content-center align-items-center p-2">
                <button
                  className="btn btn-sm btn-light me-2"
                  onClick={() => changePage(currentPage - 1)}
                  disabled={loading || currentPage === 1}
                >
                  <i className="bi bi-caret-left"></i>
                </button>
  
                <span className="text-muted" style={{ fontSize: "0.9rem" }}>
                  Page <b>{currentPage}</b> of <b>{totalPages}</b>
                </span>
  
                <button
                  className="btn btn-sm btn-light ms-2"
                  onClick={() => changePage(currentPage + 1)}
                  disabled={loading || currentPage === totalPages}
                >
                  <i className="bi bi-caret-right"></i>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
  
      {/* ✅ FIX: Modal diletak di LUAR ds-card */}
      <div
        className="modal fade"
        id="leadDetail"
        tabIndex="-1"
        aria-hidden="true"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div className="modal-dialog modal-dialog-centered ss-modal-dialog">
          <div className="modal-content ss-modal ds-modal">
            <div className="modal-header ss-modal-header">
              <h5 className="modal-title">Follow Up Update</h5>
              <button
                type="button"
                id="closemodal"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={setDefault}
              />
            </div>
  
            {!selectedLead ? (
              <div className="modal-body ss-modal-body text-start">
                <p className="text-muted mb-0">No details available.</p>
              </div>
            ) : (
              <>
                <div className="modal-body ss-modal-body text-start">
                  <div className="mb-2">
                    <div className="fw-bold">{selectedLead.name}</div>
                    <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                      {selectedLead.country}
                      {selectedLead.phone}
                    </div>
                  </div>
  
                  <div className="small text-muted mb-3">
                    <div>
                      Created: <b>{formatFollowUp12h(selectedLead.createdAt)}</b>
                    </div>
                    <div>
                      Updated: <b>{formatFollowUp12h(selectedLead.updatedAt)}</b>
                    </div>
                  </div>
  
                  {/* Status */}
                  <div className="form-floating mb-3">
                    <select
                      id="editStatus"
                      value={editedStatus}
                      onChange={(e) => setEditedStatus(e.target.value)}
                      className="form-select"
                    >
                      {statusOption.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <label htmlFor="editStatus">Status</label>
                  </div>
  
                  {/* Closed Amount */}
                  {editedStatus === "Closed" && (
                    <div className="form-floating mb-3">
                      <NumericFormat
                        id="closeValue"
                        className="form-control"
                        value={editClosedAmount}
                        thousandSeparator
                        prefix="RM "
                        allowNegative={false}
                        decimalScale={2}
                        fixedDecimalScale
                        onValueChange={(values) =>
                          setEditClosedAmount(values.value)
                        }
                        placeholder="RM 0.00"
                      />
                      <label htmlFor="closeValue">Close Value</label>
                    </div>
                  )}
  
                  {/* Remark */}
                  <div className="form-floating mb-3">
                    <textarea
                      id="editRemark"
                      className="form-control"
                      style={{ height: 140 }}
                      value={editedRemark}
                      onChange={(e) => setEditedRemark(e.target.value)}
                    />
                    <label htmlFor="editRemark">Remark</label>
                  </div>
  
                  {/* Next Follow Up */}
                  <div className="form-floating mb-3">
                    <input
                      id="editFuDate"
                      type="datetime-local"
                      className="form-control"
                      value={edittedFuDate || ""}
                      min={new Date(
                        Date.now() - new Date().getTimezoneOffset() * 60000
                      )
                        .toISOString()
                        .slice(0, 16)}
                      onChange={(e) => setEditedFuDate(e.target.value)}
                    />
                    <label htmlFor="editFuDate">Next Follow Up Date</label>
                  </div>
                </div>
  
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    data-bs-dismiss="modal"
                    onClick={setDefault}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-dark"
                    onClick={() => handleSubmit(selectedLead.id)}
                  >
                    Submit
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );  
}

export default ManagerFollowUp;
